// src/services/supportPay.js — "Support AlloApp" (payments) on the web (server/functions/supportRoutes.js).
//
// 🟢 WHO IS PAYING. The page is signed in one of two ways:
//    · this browser is already an Allo device (paired with the phone): its own sign-in;
//    · the app sent the person here: /support?c=<code> — a one-time, 5-minute code the server exchanges for a
//      sign-in of THAT account, in a separate Firebase app kept only for this tab (session persistence), so the
//      payment page never turns the browser into a paired Allo device and never outlives the tab.
// 🟢 THE MONEY IS STRIPE'S PAGE (Checkout): the card never touches allo-web. Back from it, the page watches its
//    attempt's document — the server writes "succeeded" only when Stripe's signed webhook says so.
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInWithCustomToken, setPersistence, browserSessionPersistence, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import mainApp, { auth as mainAuth } from '../config/firebase';
import { BACKEND_URL } from './socket';

const SUPPORT_APP = 'alloapp-support';
let _app = null;
function supportApp() {
  if (_app) return _app;
  _app = getApps().find((a) => a.name === SUPPORT_APP) || initializeApp(mainApp.options, SUPPORT_APP);
  return _app;
}
const supportAuth = () => getAuth(supportApp());

/** the signed-in user for this page: the code's account, else this browser's paired account */
export async function supportUser() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('c');
  const sAuth = supportAuth();
  await setPersistence(sAuth, browserSessionPersistence).catch(() => {});
  if (code) {
    try {
      const r = await fetch(`${BACKEND_URL}/api/support/redeem`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }) });
      const j = await r.json().catch(() => ({}));
      if (j && j.token) await signInWithCustomToken(sAuth, j.token);
    } catch { /* below: an expired code falls through to whoever is signed in */ }
    params.delete('c');
    const q = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
  }
  const wait = (a) => new Promise((resolve) => { const off = onAuthStateChanged(a, (u) => { off(); resolve(u); }); });
  const s = sAuth.currentUser || (await wait(sAuth));
  if (s) return { user: s, app: supportApp() };
  const m = mainAuth.currentUser || (await wait(mainAuth));
  if (m) return { user: m, app: mainApp };
  return null;
}

async function call(user, path, body) {
  const token = await user.getIdToken();
  const r = await fetch(`${BACKEND_URL}/api/support/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body || {}),
  });
  const j = await r.json().catch(() => ({ ok: false, error: 'network' }));
  return { status: r.status, ...j };
}
export const supportConfig = async () => {
  const r = await fetch(`${BACKEND_URL}/api/support/config`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ platform: 'web' }) });
  return r.json();
};
export const supportStatus = (user) => call(user, 'status');
export const startCheckout = (user, body) => call(user, 'checkout', { ...body, platform: 'web' });
export const cancelPending = (user, attemptId) => call(user, 'cancel-pending', { attemptId });
export const changeSubscription = (user, body) => call(user, 'subscription', body);
export const openPortal = (user) => call(user, 'portal');
export const setBadgeHidden = (user, hidden) => call(user, 'badge', { hidden });
export const emailReceipt = (user, paymentId) => call(user, 'receipt', { paymentId });

/** watch one attempt (the server writes it); falls back to asking the server if the listener cannot read */
export function watchAttempt(app, user, attemptId, onStatus) {
  let off = () => {};
  let timer = null;
  let stopped = false;
  const poll = async () => {
    if (stopped) return;
    try {
      const s = await supportStatus(user);
      const inHistory = (s.history || []).find((h) => h.id === attemptId);
      if (inHistory) onStatus(inHistory.status, inHistory);
      else if (s.pending && s.pending.attemptId === attemptId) onStatus(s.pending.status, s.pending);
    } catch {}
    timer = setTimeout(poll, 4000);
  };
  try {
    off = onSnapshot(doc(getFirestore(app), 'supportPayments', attemptId), (snap) => {
      if (snap.exists()) onStatus(snap.data().status, snap.data());
    }, () => { void poll(); });
  } catch { void poll(); }
  timer = setTimeout(poll, 6000);   // a second road, in case the listener is slow
  return () => { stopped = true; try { off(); } catch {} if (timer) clearTimeout(timer); };
}

/** a fresh attempt id (UUID v4) — one per "Continue", kept for every retry of the same attempt */
export function newAttemptId() {
  const c = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `att_${c.replace(/[^A-Za-z0-9]/g, '')}`.slice(0, 60);
}
